import { useTranslation } from "react-i18next";
import type { SdpStatusEntry } from "../../api/contractStatus";

// FT-1189 ④ — status chip driven by the backend status endpoint, with the
// legacy status as fallback while data loads (or if the endpoint is down).
// Renewal (D3) renders as 🔄 with a tooltip. Data-flag notices live only in the
// service-details Contract history header (Alex, 2026-07-16) — no dot in the list.

interface ContractStatusChipProps {
  entry: SdpStatusEntry | undefined;
  fallbackStatus: string | null;
}

export const statusChipStyles = (status: string) => {
  switch (status?.toLowerCase()) {
    case "active":
      return "bg-[#F9F9AE] text-[#838300]";
    case "unsigned":
      return "bg-[#FFEBEB] text-[#D04D4D]";
    case "not_generated":
      return "bg-gray-100 text-gray-500";
    case "signed":
      return "bg-primary-50 text-primary-700";
    case "onboarding":
      return "bg-[#E0F7FA] text-[#00796B]";
    case "future":
      return "bg-primary-50 text-primary-700";
    case "ended":
    case "expired":
    case "switched":
      return "bg-gray-100 text-gray-600";
    default:
      return "bg-gray-100 text-gray-500";
  }
};

const formatDateDDMMYYYY = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export const ContractStatusChip = ({ entry, fallbackStatus }: ContractStatusChipProps) => {
  const { t } = useTranslation("ActiveServices");

  const status = entry?.display_status ?? fallbackStatus;
  const label = status
    ? t(`contractStatus.${status.toLowerCase()}`, {
        defaultValue: status.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()),
      })
    : "N/A";

  const renewal = entry?.renewal ?? null;
  // The one data flag important enough for the list (Alex, 2026-07-17): contract
  // active but the market reports the EAN elsewhere. Other flags stay in the
  // Contract history header only.
  const marketMismatch = entry?.data_flags?.includes("market_status_mismatch") ?? false;

  const chip = (
    <span className={`px-2 py-1 rounded-md inline-flex items-center gap-1 ${statusChipStyles(status || "")}`}>
      {label}
      {renewal && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          className="w-3.5 h-3.5"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
          />
        </svg>
      )}
    </span>
  );

  const chipWithTooltip = renewal ? (
    <span
      className="tooltip cursor-help"
      data-tip={
        renewal.signed
          ? t("contractStatus.renewalSigned", { date: formatDateDDMMYYYY(renewal.start_date) })
          : t("contractStatus.renewalUnsigned", { date: formatDateDDMMYYYY(renewal.start_date) })
      }
    >
      {chip}
    </span>
  ) : (
    chip
  );

  if (!marketMismatch) return chipWithTooltip;

  return (
    <span className="inline-flex items-center gap-1.5">
      {chipWithTooltip}
      <span className="tooltip cursor-help" data-tip={t("contractStatus.marketMismatchTip")}>
        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
          {t("contractStatus.marketMismatchLabel")}
        </span>
      </span>
    </span>
  );
};
